import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flag, Rocket, TrendingUp, Award, Landmark, ArrowRight, Check, ShieldCheck } from "lucide-react";
import { SkillsTranslator } from "@/components/SkillsTranslator";
import { VeteranDiscountBanner } from "@/components/VeteranDiscountBanner";

const PATHS = [
  { icon: Rocket, title: "Start a business now", body: "Go from idea to launched — assessment, business plan, brand and website — all at your own pace.", to: "/start", cta: "Start free assessment" },
  { icon: TrendingUp, title: "Grow what you've built", body: "Already veteran-owned? Sharpen your brand, website, marketing and lead generation to reach more customers.", to: "/services", cta: "See growth services" },
  { icon: Award, title: "Get SDVOSB / VOSB certified", body: "Service-Disabled & Veteran-Owned Small Business certification can open doors to set-aside contracts. We'll help you get ready.", to: "/book", cta: "Talk to us about certification" },
  { icon: Landmark, title: "Find veteran funding & grants", body: "Explore veteran-specific loans, grants and SBA programs — and get a plan lenders and funders expect.", to: "/business-plan", cta: "Explore funding help" },
];

const FUNDING = [
  { title: "SDVOSB / VOSB certification", body: "Veteran-Owned and Service-Disabled Veteran-Owned certifications can qualify you for federal set-aside contracts and preferred vendor programs." },
  { title: "SBA veteran programs & VBOC", body: "The SBA's Office of Veterans Business Development and Veterans Business Outreach Centers offer training, counseling and access to capital." },
  { title: "Veteran loans & grants", body: "From veteran-focused lenders to state and nonprofit grant programs — we'll help you build a plan that stands up to a funder's questions." },
];

export default function VeteransPriorService() {
  return (
    <div data-testid="veterans-prior-service-page">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Link to="/veterans" className="inline-flex items-center gap-1.5 text-gray-400 hover:text-gold text-xs mb-5 transition-colors" data-testid="ps-back-hub"><ShieldCheck size={14} /> Veterans &amp; Retirees</Link>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05] max-w-4xl">
              Turn your service into <span className="text-gold">ownership — now.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
              Already out of the military? There's no deadline and no cohort required. Start a new business or grow the one you have — at your own pace, with help on veteran certifications and funding along the way.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Link to="/start" data-testid="ps-hero-start" className="inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors">Start your assessment <ArrowRight size={16} /></Link>
              <Link to="/book" data-testid="ps-hero-book" className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-3.5 text-sm font-semibold hover:border-gold hover:text-gold transition-colors">Book a free consult</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Discount */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12"><VeteranDiscountBanner /></section>

      {/* Paths */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-heading text-3xl text-white mb-3">Choose your path</h2>
        <p className="text-gray-400 mb-10 max-w-2xl">Wherever you are, there's a next step — pick one and we'll guide you.</p>
        <div className="grid md:grid-cols-2 gap-6">
          {PATHS.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}>
              <Link to={p.to} data-testid={`ps-path-${i}`} className="group flex flex-col h-full border border-white/10 bg-navy-800 p-7 transition-all hover:border-gold/50 hover:-translate-y-1">
                <div className="w-12 h-12 border border-gold/40 flex items-center justify-center text-gold mb-5 transition-colors group-hover:bg-gold group-hover:text-navy"><p.icon size={22} /></div>
                <h3 className="font-heading text-xl text-white mb-2">{p.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed flex-1">{p.body}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-gold text-sm font-semibold">{p.cta} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Certifications & funding */}
      <section className="border-y border-white/10 bg-navy-800/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-3"><Award size={15} /> Certifications &amp; funding</div>
          <h2 className="font-heading text-3xl text-white mb-3">Advantages you've earned</h2>
          <p className="text-gray-400 mb-10 max-w-2xl">Your service can unlock certifications and funding that civilian founders don't have access to. Here's where we help.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {FUNDING.map((f) => (
              <div key={f.title} className="border border-white/10 bg-navy p-6">
                <div className="flex items-start gap-2 mb-2"><Check size={18} className="text-gold shrink-0 mt-0.5" /><h3 className="font-heading text-lg text-white">{f.title}</h3></div>
                <p className="text-gray-400 text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SkillsTranslator />

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="border border-gold/30 bg-gold/[0.06] p-8 md:p-12 text-center">
          <Flag className="text-gold mx-auto mb-4" size={28} />
          <h2 className="font-heading text-3xl text-white mb-3">Ready when you are</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-7">Start your assessment and claim your veteran discount, or book a free consult and we'll map your path — from certification to launch.</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/start" data-testid="ps-final-start" className="inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors">Start your assessment <ArrowRight size={16} /></Link>
            <Link to="/book" data-testid="ps-final-book" className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-3.5 text-sm font-semibold hover:border-gold hover:text-gold transition-colors">Book a free consult</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
